/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Currency Utilities Module
 *
 * Provides currency symbols and formatting utilities.
 *
 * For exchange rates and currency conversion, use:
 * - CurrencyContext and useCurrency hook for React components
 * - lib/api/rates.ts for API-level operations
 *
 * @module lib/currencies
 */

// ============================================================================
// Currency Symbol Definitions
// ============================================================================

/**
 * Currency definition with code and symbol
 */
interface CurrencyDefinition {
  /** ISO 4217 currency code */
  title: string;
  /** Currency symbol for display */
  value: string;
}

/**
 * Primary currencies with symbols
 * Used for display purposes only - not for conversion
 */
const CURRENCIES: CurrencyDefinition[] = [
  {
    title: "USD",
    value: "$",
  },
  {
    title: "EUR",
    value: "€",
  },
  {
    title: "JPY",
    value: "¥",
  },
  {
    title: "GBP",
    value: "£",
  },
  {
    title: "AUD",
    value: "A$",
  },
  {
    title: "CAD",
    value: "C$",
  },
  {
    title: "CHF",
    value: "CHF",
  },
  {
    title: "CNY",
    value: "¥",
  },
  {
    title: "SEK",
    value: "kr",
  },
  {
    title: "NZD",
    value: "NZ$",
  },
  {
    title: "INR",
    value: "₹",
  },
  {
    title: "BRL",
    value: "R$",
  },
  {
    title: "MXN",
    value: "$",
  },
  {
    title: "KRW",
    value: "₩",
  },
  {
    title: "SGD",
    value: "S$",
  },
  {
    title: "HKD",
    value: "HK$",
  },
  {
    title: "NOK",
    value: "kr",
  },
  {
    title: "DKK",
    value: "kr",
  },
  {
    title: "PLN",
    value: "zł",
  },
  {
    title: "THB",
    value: "฿",
  },
  {
    title: "IDR",
    value: "Rp",
  },
  {
    title: "TRY",
    value: "₺",
  },
  {
    title: "RUB",
    value: "₽",
  },
  {
    title: "ZAR",
    value: "R",
  },
  {
    title: "AED",
    value: "د.إ",
  },
  {
    title: "SAR",
    value: "﷼",
  },
  {
    title: "PHP",
    value: "₱",
  },
  {
    title: "MYR",
    value: "RM",
  },
  {
    title: "CZK",
    value: "Kč",
  },
  {
    title: "ILS",
    value: "₪",
  },
  {
    title: "CLP",
    value: "$",
  },
  {
    title: "PKR",
    value: "₨",
  },
  {
    title: "COP",
    value: "$",
  },
  {
    title: "TWD",
    value: "NT$",
  },
  {
    title: "RON",
    value: "lei",
  },
  {
    title: "HUF",
    value: "Ft",
  },
  {
    title: "BGN",
    value: "лв",
  },
  {
    title: "VND",
    value: "₫",
  },
];

// ============================================================================
// Symbol Lookup Functions
// ============================================================================

/**
 * Get currency symbol for a given currency code
 *
 * @param currencyCode - ISO 4217 currency code (e.g., "USD", "EUR")
 * @returns Currency symbol (e.g., "$", "€") or the code itself if not found
 *
 * @example
 * ```ts
 * getCurrencySymbol("USD") // "$"
 * getCurrencySymbol("EUR") // "€"
 * getCurrencySymbol("XYZ") // "XYZ" (fallback to code)
 * ```
 */
/**
 * NF-1: currencies with no minor unit.
 *
 * Stripe sends and expects these as whole units, so an amount of 2400 means
 * JPY 2,400 and not JPY 24.00. Dividing them by one hundred understated every
 * yen and won figure by a factor of one hundred, and that reached the refund
 * dialog -- which sends the figure it was shown to the server -- so a 2,400 yen
 * order was refunded as 24 yen with no error raised anywhere.
 *
 * This mirrors _ZERO_DECIMAL_CURRENCIES in backend/apps/core/currency_format.py.
 * Both are Stripe's full zero-decimal set and the two must stay in step, so each
 * is defined once in its own half of the application and referenced everywhere
 * else rather than copied.
 */
export const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga",
  "pyg", "rwf", "ugx", "vnd", "vuv", "xaf", "xof", "xpf",
]);

/** True when the currency has no minor unit, so its amounts are whole units. */
export const isZeroDecimal = (currencyCode: string): boolean =>
  ZERO_DECIMAL_CURRENCIES.has((currencyCode || "").toLowerCase());

/**
 * Convert a Stripe amount into the units a customer reads.
 *
 * Zero-decimal currencies arrive already in whole units; everything else arrives
 * in minor units and is divided by one hundred. Every display path should use
 * this rather than dividing directly, so the rule lives in one place.
 */
export const toDisplayAmount = (currencyCode: string, amount: number): number =>
  isZeroDecimal(currencyCode) ? Math.round(amount) : amount / 100;

export const getCurrencySymbol = (currencyCode: string): string => {
  if (!currencyCode) return "$";
  const normalizedCode = currencyCode.toUpperCase();
  const currency = CURRENCIES.find((c) => c.title === normalizedCode);
  return currency?.value || normalizedCode;
};

/**
 * S663 (SU-5 follow-up): is this currency's symbol shared with another currency
 * the shop can settle in?
 *
 * Ten of the thirty-eight currencies in the table above share three symbols:
 * `$` is USD, MXN, CLP and COP; `kr` is SEK, NOK and DKK; `¥` is JPY and CNY.
 * For those, the rendered figure alone does not say which currency it is.
 *
 * That mattered less while the Invoices column heading claimed to name one. It
 * named the WRONG one -- "(USD)" over a euro figure is what Susmita T. reported
 * -- and SU-5 removed it, on the reasoning that "the row states its own
 * currency". For `€61.81` that is true. For `$61.81` it is not: a customer
 * billed in Mexican pesos now reads a dollar sign with nothing else to go on,
 * and reconciling against a bank statement is precisely the task SU-5 exists to
 * make possible. Removing a wrong statement must not leave no statement.
 */
/**
 * Every currency code the shop can render a figure in.
 *
 * Exported so a test can DERIVE which symbols collide rather than restating a
 * list that would go stale the day a currency is added. The table itself stays
 * private -- callers ask questions about it, they do not read it.
 */
export const CURRENCY_CODES: readonly string[] = CURRENCIES.map((c) => c.title);

export const hasAmbiguousCurrencySymbol = (currencyCode: string): boolean => {
  if (!currencyCode) return false;
  const symbol = getCurrencySymbol(currencyCode.toUpperCase());
  if (!symbol) return false;
  return (
    CURRENCIES.filter((c) => c.value === symbol).length > 1
  );
};

/**
 * A rendered money figure, with its currency code when the symbol alone does not
 * say which currency it is.
 *
 * Takes the ALREADY-FORMATTED string rather than the amount, deliberately. The
 * rendering rule -- the symbol, the grouping, the decimal mark, the shop's
 * active language -- lives in `formatCurrency`, and this must not become a
 * second place that decides any of it. What lives here is the one thing this
 * module already knows: which symbols are shared.
 *
 * It is here rather than beside `formatCurrency` for a second reason worth
 * knowing. `@/lib/utils` is hand-mocked with a partial factory by more than a
 * dozen test files, so ADDING AN EXPORT TO IT BREAKS EVERY ONE OF THEM -- the
 * mock returns `undefined` for the new name and the caller crashes. A shared
 * rule that has to be reachable from a module under partial mock belongs on the
 * side that is not mocked.
 *
 * United States dollars are left unqualified. The catalogue is authored in USD
 * and every price the shop quotes is a dollar price, so an unqualified `$` on
 * this shop conventionally means dollars -- which is what the "(USD)" heading
 * SU-5 removed was telling the customer, and the one thing it got right. The
 * ambiguity worth answering is the other direction: a customer whose bank
 * statement is in Mexican, Chilean or Colombian pesos reading a dollar sign with
 * no way to tell it is theirs.
 */
export const withCurrencyCode = (currencyCode: string, formatted: string): string => {
  const code = (currencyCode || "").toUpperCase();
  if (!code || code === "USD") return formatted;
  if (!hasAmbiguousCurrencySymbol(code)) return formatted;
  return `${formatted} ${code}`;
};

/**
 * Check if a currency code is in our symbol list
 *
 * @param currencyCode - ISO 4217 currency code
 * @returns true if we have a symbol for this currency
 */
const hasCurrencySymbol = (currencyCode: string): boolean => {
  if (!currencyCode) return false;
  const normalizedCode = currencyCode.toUpperCase();
  return CURRENCIES.some((c) => c.title === normalizedCode);
};

/**
 * Get currency definition by code
 *
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency definition or undefined if not found
 */
const getCurrencyByCode = (
  currencyCode: string,
): CurrencyDefinition | undefined => {
  if (!currencyCode) return undefined;
  const normalizedCode = currencyCode.toUpperCase();
  return CURRENCIES.find((c) => c.title === normalizedCode);
};

// ============================================================================
// Exports
// ============================================================================

export default {
  CURRENCIES,
  getCurrencySymbol,
  hasCurrencySymbol,
  getCurrencyByCode,
};
